/**
 * Crash beacon (docs/design/06 §6, 01 §9): uncaught errors and unhandled rejections are posted to
 * `/api/beacon` with the build SHA and the current match position, at most a few per page view.
 */
import { BeaconSchema } from '@play/protocol';
import { apiUrl } from './api';
import { env } from './env';

/** Where the player is, set by the match screen. */
export const beaconContext: { matchId: string | null; seat: `p${number}` | null; seq: number | null } = {
  matchId: null,
  seat: null,
  seq: null,
};

let sent = 0;
const MAX_PER_PAGE = 5;

function send(message: string, stack: string | null): void {
  if (sent >= MAX_PER_PAGE || import.meta.env.DEV || import.meta.env.MODE === 'test') return;
  sent++;
  const body = BeaconSchema.safeParse({
    sha: env.sha.slice(0, 64),
    matchId: beaconContext.matchId,
    seat: beaconContext.seat,
    seq: beaconContext.seq,
    message: message.slice(0, 2000),
    stack: stack?.slice(0, 8000) ?? null,
    userAgent: navigator.userAgent.slice(0, 400),
  });
  if (!body.success) return;
  const payload = JSON.stringify(body.data);
  if (!navigator.sendBeacon(apiUrl('/api/beacon'), new Blob([payload], { type: 'application/json' }))) {
    void fetch(apiUrl('/api/beacon'), {
      method: 'POST',
      body: payload,
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
    }).catch(() => undefined);
  }
}

/** Installs the global error handlers once. */
export function installBeacon(): void {
  window.addEventListener('error', (e) => {
    send(e.message || 'error', e.error instanceof Error ? (e.error.stack ?? null) : null);
  });
  window.addEventListener('unhandledrejection', (e) => {
    const reason: unknown = e.reason;
    send(
      reason instanceof Error ? reason.message : String(reason),
      reason instanceof Error ? (reason.stack ?? null) : null,
    );
  });
}
